import { FormEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useAsync } from "react-use";
import {
	Alert,
	AlertTitle,
	Card,
	Collapse,
	Fab,
	Grow,
	IconButton,
	InputAdornment,
	LinearProgress,
	Stack,
	TextField,
	Typography,
	styled,
	useMediaQuery,
	lighten,
} from "@mui/material";
import { useTheme } from "@mui/styles";

import { getToken } from "auth/helpers/authApi";

import { apiClient } from "utils/apiClient";

import { AIChatSession, ChatAIStatus, ChatHistory } from "user-dashboard/types";

import { useAuthUser } from "context/GameAuthContext";

import { ReactComponent as SendIcon } from "assets/svg/send.svg";
import { ReactComponent as XIcon } from "assets/svg/x.svg";
import { ReactComponent as ChatBubbleIcon } from "assets/svg/chat-bubble.svg";
import { ReactComponent as ArrowLeftIcon } from "assets/svg/arrow-left.svg";

import { ChatHistoryItem } from "./ChatHistoryItem";
import { getInvertColor } from "utils/themeUtils";

const FloatingContainer = styled("div")({
	position: "fixed",
	bottom: "3.2rem",
	right: "3.2rem",
});

const ChatCard = styled(Card)({
	width: "47.3rem",
	height: "45.4rem",
	borderRadius: "0.8rem",
	boxShadow: "-0.2rem 0.2rem 2.7rem rgba(0, 0, 0, 0.4)",
});

export default function FloatingChatWithAI({ organizationName, logo }: { organizationName: string; logo: string }) {
	const intl = useIntl();
	const theme = useTheme();
	const matchesMobile = useMediaQuery("(max-width:34em)");
	const user = useAuthUser();

	const [showChat, setShowChat] = useState(false);
	const [showFAB, setShowFAB] = useState(true);
	const [sessionId, setSessionId] = useState("");

	const { loading, value: status } = useAsync(async () => {
		const result = await apiClient("organizations/chat-ai/status", { token: getToken() });
		return result.data?.status as ChatAIStatus;
	}, []);

	const openChat = () => {
		setShowFAB(false);
		window.setTimeout(() => {
			setShowChat(true);
		}, 250);
	};

	const closeChat = () => {
		setShowChat(false);
		window.setTimeout(() => {
			setShowFAB(true);
		}, 250);
	};

	useEffect(() => {
		if (!sessionId) {
			setSessionId(user.id + "-" + Date.now());
		}
	}, [sessionId, user.id]);

	if (loading || status !== ChatAIStatus.COMPLETED || !sessionId) {
		return null;
	}

	return (
		<>
			<FloatingContainer>
				<Grow
					in={showFAB}
					style={{
						padding: "0.8rem 2.4rem",
						background: `linear-gradient(135deg, ${lighten(theme.palette.primary.main, 0.3)}, ${
							theme.palette.primary.main
						} 45%)`,
						boxShadow: `0 0.4rem 0.8rem rgba(43, 70, 115, 0.5)
						`,
					}}
					className="user-dashboard-AI-chat-button"
				>
					<Fab
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "0.8rem",
							zIndex: showChat ? undefined : 10,

							"& svg path": {
								fill: (theme) => getInvertColor(theme.palette.primary.main),
							},
						}}
						variant="extended"
						color="primary"
						onClick={openChat}
					>
						<ChatBubbleIcon width="2rem" height="1.8rem" />
						<Typography
							component="span"
							fontWeight={700}
							fontSize="1.6rem"
							color={(theme) => getInvertColor(theme.palette.primary.main)}
						>
							{intl.formatMessage({ id: "USER-DASHBOARD.chat-aipanel.fab.chat", defaultMessage: "Chat" })}
						</Typography>
					</Fab>
				</Grow>
			</FloatingContainer>
			<FloatingContainer
				className="user-dashboard-AI-chat-container"
				sx={{
					...(matchesMobile ? { top: 0, left: 0, width: "100%", zIndex: showChat ? undefined : -1 } : {}),
				}}
			>
				{matchesMobile ? (
					<ChatCard
						sx={{
							width: "100%",
							height: "100%",
							borderRadius: 0,
							opacity: showChat ? 1 : 0,
							pointerEvents: showChat ? "all" : "none",
							transition: "opacity 150ms ease-in",
						}}
					>
						<Stack sx={{ height: "100%" }}>
							<Stack flexDirection="row" alignItems="center" padding="3.2rem 2rem" gap="1.6rem">
								<IconButton onClick={closeChat} sx={{ width: "3.2rem", height: "3.2rem", p: 0 }}>
									<ArrowLeftIcon width="1.6rem" height="1.2rem" />
								</IconButton>
								<Typography fontWeight={700} fontSize="2rem" color="grey.600">
									{intl.formatMessage(
										{ id: "USERDASHBOARD.header.searchbar" },
										{ company: organizationName || "Riddlestory" },
									)}
								</Typography>
							</Stack>
							<ChatAIPanel sessionId={sessionId} logo={logo} />
						</Stack>
					</ChatCard>
				) : (
					<Collapse in={showChat}>
						<ChatCard sx={{ width: showChat ? "47.3rem" : 0 }}>
							<Stack sx={{ height: "100%" }}>
								<Stack
									flexDirection="row"
									alignItems="center"
									justifyContent="space-between"
									padding="1.6rem 2rem"
								>
									<Typography fontWeight={700} fontSize="2rem" color="grey.600">
										{intl.formatMessage(
											{ id: "USERDASHBOARD.header.searchbar" },
											{ company: organizationName || "Riddlestory" },
										)}
									</Typography>
									<IconButton onClick={closeChat}>
										<XIcon />
									</IconButton>
								</Stack>
								<ChatAIPanel sessionId={sessionId} logo={logo} />
							</Stack>
						</ChatCard>
					</Collapse>
				)}
			</FloatingContainer>
		</>
	);
}

const ChatBodyContainer = styled("div")(({ theme }) => ({
	flex: 1,
	overflowY: "auto",
	padding: "1.6rem",
	backgroundColor: theme.palette.primary["100"],
	scrollbarGutter: "stable",
}));

const ChatInputContainer = styled("div")({
	padding: "1.6rem 2rem",
});

function ChatAIPanel({ sessionId, logo }: { sessionId: string; logo: string }) {
	const intl = useIntl();

	const questionInputRef = useRef<HTMLInputElement>();
	const chatMessageBodyRef = useRef<HTMLDivElement>(null);

	const [chatHistories, setChatHistories] = useState<ChatHistory[]>([]);
	const [isLoadingHistories, setIsLoadingHistories] = useState<boolean>(false);
	const [isSendingQuestion, setIsSendingQuestion] = useState<boolean>(false);
	const [errorObj, setErrorObj] = useState<Error>();

	useEffect(() => {
		const fetchHistories = async () => {
			setIsLoadingHistories(true);
			try {
				// Fetch chat histories
				const result: { data: AIChatSession } = await apiClient(`chat-ai/history/${sessionId}`, {
					token: getToken(),
				});

				if (result.data?.chatHistory?.length > 0) {
					setChatHistories(result.data?.chatHistory || []);
				}
			} catch (err: any) {
				setErrorObj(err);
			} finally {
				setIsLoadingHistories(false);
			}
		};

		// Fetch chat histories
		fetchHistories();
	}, [sessionId]);

	useEffect(() => {
		if (!chatMessageBodyRef.current) {
			return;
		}
		// Put the scroll at the very bottom every time chat
		chatMessageBodyRef.current.scrollTop = chatMessageBodyRef.current.scrollHeight;
	}, [chatHistories]);

	const onSend = async (evt: FormEvent<HTMLFormElement>) => {
		evt.preventDefault();

		const questionText = questionInputRef.current?.value;

		if (!questionText || isLoadingHistories || isSendingQuestion) {
			return;
		}

		try {
			setIsSendingQuestion(true);
			// Add the question temporarily to the UI
			setChatHistories((prev) => [...prev, { _id: "", query: questionText, answer: "" }]);
			const result: { data: AIChatSession } = await apiClient(`chat-ai/${sessionId}`, {
				data: {
					query: questionText,
				},
				token: getToken(),
			});
			setChatHistories(result.data?.chatHistory || []);
			// Put the scroll at the very bottom
			chatMessageBodyRef.current!.scrollTop = chatMessageBodyRef.current!.scrollHeight;
		} catch (err: any) {
			setErrorObj(err);
		} finally {
			setIsSendingQuestion(false);
			questionInputRef.current!.focus();
		}

		questionInputRef.current!.value = "";
	};

	return (
		<>
			{errorObj?.message && (
				<Alert severity="error">
					<AlertTitle>
						{intl.formatMessage({
							id: "USER-DASHBOARD.chat-aipanel.error",
						})}
					</AlertTitle>
					Something went wrong - {errorObj?.message}
				</Alert>
			)}
			<ChatBodyContainer ref={chatMessageBodyRef} className="scrollbar user-dashboard-AI-chat-body-container">
				{isLoadingHistories && <LinearProgress />}
				<ChatHistoryItem
					chatHistory={{
						query: "",
						answer: intl.formatMessage({ id: "USER-DASHBOARD.chat-aipanel.welcome-message" }),
						createdAt: new Date(),
					}}
					aiLogo={logo}
				/>
				{chatHistories.map((chatHistory) => {
					return <ChatHistoryItem key={chatHistory._id} chatHistory={chatHistory} aiLogo={logo} />;
				})}
			</ChatBodyContainer>
			<ChatInputContainer className="user-dashboard-AI-chat-input-container">
				<form onSubmit={onSend}>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: "common.white",
								pr: "0.8rem",
								fontSize: "1.6rem",
								borderRadius: "0.8rem",
							},
							"& .MuiOutlinedInput-input::placeholder": {
								opacity: 1,
								color: "grey.400",
								transition: "color 150ms ease-in",
							},
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "grey.200",
								transition: "all 150ms ease-in",
							},
							"&:hover svg path, & .MuiOutlinedInput-input:focus + .MuiInputAdornment-root .MuiButtonBase-root svg path": {
								fill: (theme) => `${theme.palette.primary.main}`,
							},
							"&:hover .MuiOutlinedInput-input::placeholder, & .MuiOutlinedInput-input:focus::placeholder": {
								color: "currentColor",
							},
						}}
						disabled={isSendingQuestion}
						placeholder={intl.formatMessage({
							id: "USER-DASHBOARD.chat-aipanel.text-input.placeholder",
							defaultMessage: "Ask me anything!",
						})}
						inputRef={questionInputRef}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										type="submit"
										sx={{
											"& svg path": {
												transition: "all 150ms ease-in",
											},
											"&.Mui-disabled": {
												pointerEvents: "unset", // allow :hover styles to be triggered
												cursor: "not-allowed", // and custom cursor can be defined without :hover state
											},
										}}
									>
										<SendIcon width="2rem" height="2rem" />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</form>
			</ChatInputContainer>
		</>
	);
}
